import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Navigate,
  useLocation,
} from 'react-router-dom';
import { Layout } from 'spark-core-dx/components';
import { useGenericManager } from 'spark-core-dx/hooks';
import {
  buildClientCommandRoute,
  CLIENT_COMMAND_SCOPE_TYPES,
  getClientCommandRouteScope,
} from './clientCommandScreenRegistry';
import {
  CLIENT_COMMAND_ACCESS_MODES,
} from './commandAccess';
import {
  AllClientsScreen,
  ClientCommandCenterScreen,
  ClientInformationScreen,
} from './ClientEntryScreens';
import ClientFilesScreen from './ClientFilesScreen';
import ClientCommunicationsScreen from './ClientCommunicationsScreen';
import {
  ClientAuthenticationScreen,
  ClientRolesPermissionsScreen,
  ClientUserProfilesScreen,
} from './ClientSecurityScreens';
import {
  ClientDatabasePartitionScreen,
  ClientDomainsScreen,
  ClientThemeScreen,
} from './ClientConfigurationScreens';
import ClientResourcesEntitlementsScreen from './ClientResourcesEntitlementsScreen';
import ClientBillingScreen from './ClientBillingScreen';
import ClientActivityScreen from './ClientActivityScreen';
import ClientActiveSessionsScreen from './ClientActiveSessionsScreen';
import ClientMarketplacePaymentsScreen from './ClientMarketplacePaymentsScreen';
import {
  CLIENT_COMMAND_IMPLEMENTED_SCREEN_KEYS,
} from './clientCommandImplementedScreens';
import {
  CLIENT_COMMAND_ITEM_NAMES,
  CLIENT_COMMAND_OPERATIONS,
  getFirstResultItem,
  getResultMessage,
  isSuccessfulResult,
} from './clientEntryContracts';
import '../resource/workspace/ResourceWorkspace.scss';
import './commandShell.scss';

const ClientCommandScreenFoundation = ({
  screen,
  screens,
  currentEnvironment,
  clientId = null,
  appId = null,
}) => {
  const headerManager = useGenericManager(
    CLIENT_COMMAND_ITEM_NAMES.commandCenter,
  );
  const headerManagerRef = useRef(headerManager);
  const [selectedClientHeader, setSelectedClientHeader] = useState(null);
  headerManagerRef.current = headerManager;

  const updateSelectedClientHeader = useCallback((header) => {
    const title = String(header?.title || '').trim();
    if (!title) return;
    setSelectedClientHeader({
      clientId,
      title,
    });
  }, [clientId]);

  useEffect(() => {
    if (
      !clientId
      || screen.CanView !== true
      || screen.Key === 'command-center'
    ) {
      return undefined;
    }

    let active = true;
    const loadSelectedClientHeader = async () => {
      const manager = headerManagerRef.current;
      if (!manager?.RunOperation) return;

      try {
        const result = await manager.RunOperation({
          operationName: CLIENT_COMMAND_OPERATIONS.loadClientCommandCenter,
          item: {
            ClientId: clientId,
            CurrentEnvironment: currentEnvironment,
          },
          isItem: true,
          isWarning: true,
          supressAllErrors: false,
        });
        if (!isSuccessfulResult(result)) {
          throw new Error(getResultMessage(
            result,
            'The selected client header is unavailable.',
          ));
        }

        const title = String(
          getFirstResultItem(result)?.OfficialName || '',
        ).trim();
        if (active && title) {
          setSelectedClientHeader({
            clientId,
            title,
          });
        }
      } catch {
        // The active screen owns its visible load failure. Keep the shared
        // heading empty when the authoritative client identity is unavailable.
      }
    };

    loadSelectedClientHeader();
    return () => {
      active = false;
    };
  }, [
    clientId,
    currentEnvironment,
    screen.CanView,
    screen.Key,
  ]);

  if (!CLIENT_COMMAND_IMPLEMENTED_SCREEN_KEYS.includes(screen.Key)) {
    return (
      <section className="command-shell__surface" role="alert">
        <h2>Unsupported Client Command screen</h2>
        <p>The server registry returned a screen that this Command build does not implement.</p>
      </section>
    );
  }

  if (!screen.CanView) {
    return (
      <section className="command-shell command-shell__surface" role="alert">
        <h2>{screen.DisplayName}</h2>
        <p>The required area-specific read permission is not available.</p>
      </section>
    );
  }

  const hasCurrentClientHeader =
    selectedClientHeader?.clientId === clientId;
  const headerEyebrow = screen.DisplayName;
  const headerTitle = hasCurrentClientHeader
    ? selectedClientHeader.title
    : '';
  const clientScreenHeader = (
    <header className="resource-workspace-list-header">
      <div className="resource-workspace-list-header-title resource-workspace-section-title">
        <span className="resource-workspace-eyebrow">{headerEyebrow}</span>
        {headerTitle ? (
          <h1 id={`screen-${screen.Key}`}>{headerTitle}</h1>
        ) : null}
        {screen.Description ? <p>{screen.Description}</p> : null}
      </div>
    </header>
  );
  const usesAIWorkspace = screen.Key === 'activity-logs'
    || screen.Key === 'billing';

  const implementedScreen = {
    'all-clients': (
      <AllClientsScreen
        screen={screen}
        onboardingScreen={screens.find((entry) => entry.Key === 'onboard-client')}
        currentEnvironment={currentEnvironment}
      />
    ),
    'onboard-client': (
      <AllClientsScreen
        screen={screens.find((entry) => entry.Key === 'all-clients')}
        onboardingScreen={screen}
        currentEnvironment={currentEnvironment}
        openOnboardingInitially
      />
    ),
    'command-center': (
      <ClientCommandCenterScreen
        screen={screen}
        screens={screens}
        clientId={clientId}
        currentEnvironment={currentEnvironment}
        onHeaderChange={updateSelectedClientHeader}
      />
    ),
    'client-information': (
      <ClientInformationScreen
        screen={screen}
        clientId={clientId}
        currentEnvironment={currentEnvironment}
        onHeaderChange={updateSelectedClientHeader}
      />
    ),
    domains: (
      <ClientDomainsScreen
        screen={screen}
        clientId={clientId}
        currentEnvironment={currentEnvironment}
      />
    ),
    'database-partition': (
      <ClientDatabasePartitionScreen
        screen={screen}
        clientId={clientId}
        appId={appId}
        currentEnvironment={currentEnvironment}
      />
    ),
    theme: (
      <ClientThemeScreen
        screen={screen}
        clientId={clientId}
        appId={appId}
        currentEnvironment={currentEnvironment}
      />
    ),
    'client-files': (
      <ClientFilesScreen
        screen={screen}
        clientId={clientId}
        currentEnvironment={currentEnvironment}
      />
    ),
    communications: (
      <ClientCommunicationsScreen
        screen={screen}
        clientId={clientId}
        appId={appId}
        currentEnvironment={currentEnvironment}
      />
    ),
    authentication: (
      <ClientAuthenticationScreen
        screen={screen}
        clientId={clientId}
        appId={appId}
        currentEnvironment={currentEnvironment}
      />
    ),
    'active-sessions': (
      <ClientActiveSessionsScreen
        screen={screen}
        clientId={clientId}
        currentEnvironment={currentEnvironment}
      />
    ),
    'roles-permissions': (
      <ClientRolesPermissionsScreen
        screen={screen}
        clientId={clientId}
        appId={appId}
        currentEnvironment={currentEnvironment}
      />
    ),
    'user-profiles': (
      <ClientUserProfilesScreen
        screen={screen}
        clientId={clientId}
        appId={appId}
        currentEnvironment={currentEnvironment}
      />
    ),
    'resources-entitlements': (
      <ClientResourcesEntitlementsScreen
        screen={screen}
        clientId={clientId}
        currentEnvironment={currentEnvironment}
      />
    ),
    'marketplace-payments': (
      <ClientMarketplacePaymentsScreen
        screen={screen}
        clientId={clientId}
        currentEnvironment={currentEnvironment}
      />
    ),
    billing: (
      <ClientBillingScreen
        screen={screen}
        clientId={clientId}
        currentEnvironment={currentEnvironment}
        workspaceHeader={clientScreenHeader}
      />
    ),
    'activity-logs': (
      <ClientActivityScreen
        screen={screen}
        clientId={clientId}
        currentEnvironment={currentEnvironment}
        workspaceHeader={clientScreenHeader}
      />
    ),
  }[screen.Key];

  if (screen.Key === 'all-clients' || screen.Key === 'onboard-client') {
    return implementedScreen;
  }

  return (
    <main
      className={`resource-workspace command-client-workspace${
        screen.Key === 'client-files' ? ' command-client-workspace--files' : ''
      }${
        screen.Key === 'user-profiles' ? ' command-client-workspace--user-profiles' : ''
      }${
        usesAIWorkspace ? ' command-client-workspace--ai' : ''
      }`}
      aria-labelledby={headerTitle ? `screen-${screen.Key}` : undefined}
      aria-label={
        !headerTitle
          ? `${screen.DisplayName} for selected client`
          : undefined
      }
    >
      {usesAIWorkspace ? null : clientScreenHeader}
      {implementedScreen || (
        <section className="resource-workspace-panel command-shell__capability">
          <div className="resource-workspace-section-title">
            <h2>Screen implementation pending</h2>
            <p>
            The canonical route, scope, and permission boundary are active. The bounded
            screen implementation is delivered by its dependent Client Command task.
            </p>
          </div>
        </section>
      )}
    </main>
  );
};

const CommandRouteContent = ({ registry, access }) => {
  const location = useLocation();
  const pathname = location.pathname;
  const activeScope = getClientCommandRouteScope(
    registry.screens,
    pathname,
  );
  const isOwnClientMode =
    access?.mode === CLIENT_COMMAND_ACCESS_MODES.OWN_CLIENT;
  const ownClientRoute = isOwnClientMode
    ? `/command/clients/${access.clientId}`
    : null;
  const isOutsideOwnClientScope = isOwnClientMode && (
    !activeScope
    || activeScope.screen?.ScopeType === 'Global'
    || activeScope.clientId !== access.clientId
    || (
      activeScope.screen?.RequiresApplication
      && activeScope.appId !== access.appId
    )
  );
  const lacksRequiredScreenAccess =
    activeScope?.screen?.RequiresAccess === true
    && activeScope.screen.CanAccess !== true;
  const accessibleFallbackRoute = lacksRequiredScreenAccess
    ? registry.screens
      .filter((screen) => (
        screen.ScopeType !== CLIENT_COMMAND_SCOPE_TYPES.GLOBAL
        && (screen.RequiresAccess !== true || screen.CanAccess === true)
      ))
      .map((screen) => buildClientCommandRoute(screen, {
        clientId: activeScope?.clientId || access?.clientId,
        appId: activeScope?.appId || access?.appId,
      }))
      .find((route) => route && route !== pathname)
    : null;
  const screenContent = isOutsideOwnClientScope ? (
    <Navigate to={ownClientRoute} replace />
  ) : pathname === '/command' || !activeScope ? (
    <Navigate to="/command/clients" replace />
  ) : lacksRequiredScreenAccess && accessibleFallbackRoute ? (
    <Navigate to={accessibleFallbackRoute} replace />
  ) : lacksRequiredScreenAccess ? (
    <section className="command-shell__surface" role="alert">
      <h2>Client Command access denied</h2>
      <p>You do not have access to any selected-client screen.</p>
    </section>
  ) : activeScope.hasInvalidClientId || activeScope.hasInvalidAppId ? (
    <section className="command-shell__surface" role="alert">
      <h2>Invalid Client Command scope</h2>
      <p>The requested client or application identifier is not valid.</p>
    </section>
  ) : (
    <ClientCommandScreenFoundation
      screen={activeScope.screen}
      screens={registry.screens}
      currentEnvironment={registry.currentEnvironment}
      clientId={activeScope.clientId}
      appId={activeScope.appId}
    />
  );

  return screenContent;
};

const CommandShell = ({ registry, access = null }) => {
  return (
    <Layout layoutOverride="standard">
      <CommandRouteContent registry={registry} access={access} />
    </Layout>
  );
};

export const CommandAccessDenied = () => (
  <Layout layoutOverride="standard">
    <section className="command-shell command-shell--message" role="alert">
      <p className="command-shell__eyebrow">Convoke Administration</p>
      <h1>Command access denied</h1>
      <p>
        Client Command requires cross-client management authorization or
        own-client administration access.
      </p>
    </section>
  </Layout>
);

export const CommandRegistryError = ({ message }) => (
  <Layout layoutOverride="standard">
    <section className="command-shell command-shell--message" role="alert">
      <p className="command-shell__eyebrow">Convoke Administration</p>
      <h1>Command unavailable</h1>
      <p>
        {message || 'The Client Command screen registry is unavailable.'}
      </p>
    </section>
  </Layout>
);

export default CommandShell;
